import React, { useState, useEffect } from 'react';
import { X, Upload, Calendar, Clock, User, CheckCircle } from 'lucide-react';
import ConfirmationModal from '../../ConfirmationModal';
import { useToast } from '../../Toast';
import { useAuth } from '../../../contexts/AuthContext';
import FirebaseTaskService, { PracticeTask } from '../../../services/firebase/FirebaseTaskService';
import { FirebasePatientService } from '../../../services/firebase/FirebasePatientService';
import TeamMemberService, { TeamMember } from '../../../services/TeamMemberService';
import VoiceInputButton from '../../VoiceInputButton';

interface TasksTabProps {
  practiceOwnerId?: string;
}

const TasksTab: React.FC<TasksTabProps> = ({ practiceOwnerId }) => {
  const toast = useToast();
  const { user, userProfile } = useAuth();
  const providerId = user?.uid || '';
  const ownerIdForQuery = practiceOwnerId || providerId;

  // Data
  const [tasks, setTasks] = useState<PracticeTask[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [clients, setClients] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);

  // UI state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [taskToComplete, setTaskToComplete] = useState<string | null>(null);
  const [completionNotes, setCompletionNotes] = useState('');
  const [viewFilter, setViewFilter] = useState<'all' | 'mine' | 'completed'>('all');

  // Form
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    dueDate: '',
    dueTime: '',
    priority: 'none' as 'none' | 'low' | 'medium' | 'high',
    assignedToId: '',
    assignedToName: '',
    clientId: '',
    clientName: '',
  });

  // Subscribe to tasks from Firestore
  useEffect(() => {
    if (!ownerIdForQuery) return;
    setLoading(true);
    const unsub = FirebaseTaskService.subscribeToTasks(ownerIdForQuery, (fetchedTasks) => {
      setTasks(fetchedTasks);
      setLoading(false);
    });
    return () => unsub();
  }, [ownerIdForQuery]);

  // Load team members
  useEffect(() => {
    const service = TeamMemberService.getInstance();
    const update = () => setTeamMembers(service.getAllTeamMembers());
    service.subscribe(update);
    update(); // initial load
    return () => service.unsubscribe(update);
  }, []);

  // Load clients
  useEffect(() => {
    if (!ownerIdForQuery) return;
    FirebasePatientService.getAllPracticePatients().then(patients => {
      setClients(patients.map(p => ({
        id: p.id || '',
        name: `${p.firstName} ${p.lastName}`.trim(),
      })));
    }).catch(() => {
      // Fallback to provider-specific
      FirebasePatientService.getAllPatients(providerId).then(patients => {
        setClients(patients.map(p => ({
          id: p.id || '',
          name: `${p.firstName} ${p.lastName}`.trim(),
        })));
      }).catch(() => {});
    });
  }, [ownerIdForQuery, providerId]);

  const currentUserName = userProfile?.displayName || user?.displayName || userProfile?.email?.split('@')[0] || 'Unknown';
  const userRole = (userProfile as any)?.role || '';
  const isAdminOrDirector = ['system_admin', 'clinical_director'].includes(userRole);

  const resetForm = () => {
    setTaskForm({
      title: '',
      description: '',
      dueDate: '',
      dueTime: '',
      priority: 'none',
      assignedToId: '',
      assignedToName: '',
      clientId: '',
      clientName: '',
    });
  };

  const handleCreateTask = async () => {
    if (!taskForm.title.trim()) {
      toast.warning('Missing Name', 'Task name is required.');
      return;
    }

    try {
      await FirebaseTaskService.createTask({
        title: taskForm.title.trim(),
        description: taskForm.description.trim(),
        dueDate: taskForm.dueDate,
        dueTime: taskForm.dueTime || undefined,
        priority: taskForm.priority,
        status: 'pending',
        assignedToId: taskForm.assignedToId || undefined,
        assignedToName: taskForm.assignedToName || undefined,
        assignedById: providerId,
        assignedByName: currentUserName,
        clientId: taskForm.clientId || undefined,
        clientName: taskForm.clientName || undefined,
        practiceOwnerId: ownerIdForQuery,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      toast.success('Task Created', taskForm.assignedToName
        ? `Task assigned to ${taskForm.assignedToName}.`
        : 'Task created successfully.');
      setShowCreateModal(false);
      resetForm();
    } catch (err) {
      console.error('Error creating task:', err);
      toast.error('Error', 'Failed to create task.');
    }
  };

  const handleCompleteClick = (taskId: string) => {
    setTaskToComplete(taskId);
    setCompletionNotes('');
    setShowCompleteModal(true);
  };

  const handleConfirmComplete = async () => {
    if (!taskToComplete) return;
    try {
      await FirebaseTaskService.completeTask(taskToComplete, completionNotes);
      toast.success('Task Completed', 'Task marked as done.');
    } catch (err) {
      toast.error('Error', 'Failed to complete task.');
    }
    setShowCompleteModal(false);
    setTaskToComplete(null);
    setCompletionNotes('');
  };

  const handleReopenTask = async (taskId: string) => {
    try {
      await FirebaseTaskService.reopenTask(taskId);
      toast.info('Task Reopened', 'Task moved back to pending.');
    } catch (err) {
      toast.error('Error', 'Failed to reopen task.');
    }
  };

  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;
    try {
      await FirebaseTaskService.deleteTask(taskToDelete);
      toast.success('Task Deleted', 'Task removed.');
    } catch (err) {
      toast.error('Error', 'Failed to delete task.');
    }
    setTaskToDelete(null);
  };

  const handleAssigneeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const memberId = e.target.value;
    if (!memberId) {
      setTaskForm({ ...taskForm, assignedToId: '', assignedToName: '' });
      return;
    }
    const member = teamMembers.find(m => m.id === memberId || m.authUid === memberId);
    setTaskForm({
      ...taskForm,
      assignedToId: member?.authUid || member?.id || memberId,
      assignedToName: member?.name || '',
    });
  };

  const handleClientChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const clientId = e.target.value;
    const client = clients.find(c => c.id === clientId);
    setTaskForm({
      ...taskForm,
      clientId,
      clientName: client?.name || '',
    });
  };

  // Visibility: admin/director see all tasks; providers only see tasks they created or are assigned to
  const visibleTasks = isAdminOrDirector
    ? tasks
    : tasks.filter(t => t.assignedToId === providerId || t.assignedById === providerId);

  // Filter tasks
  const filteredTasks = visibleTasks.filter(task => {
    if (viewFilter === 'completed') return task.status === 'completed';
    if (viewFilter === 'mine') {
      return task.status !== 'completed' && task.assignedToId === providerId;
    }
    // 'all' — show pending/in_progress
    return task.status !== 'completed';
  });

  // Sort: overdue first, then by due date, then by priority
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    // Completed tasks at bottom
    if (a.status === 'completed' && b.status !== 'completed') return 1;
    if (b.status === 'completed' && a.status !== 'completed') return -1;
    // High priority first
    const priOrder: Record<string, number> = { high: 0, medium: 1, low: 2, none: 3 };
    const priDiff = (priOrder[a.priority] || 3) - (priOrder[b.priority] || 3);
    if (priDiff !== 0) return priDiff;
    // Earlier due date first
    if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate);
    if (a.dueDate) return -1;
    if (b.dueDate) return 1;
    return 0;
  });

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const isOverdue = (dueDate: string) => {
    if (!dueDate) return false;
    return new Date(dueDate + 'T23:59:59') < new Date();
  };

  const formatDueDate = (dueDate: string) => {
    if (!dueDate) return '';
    const d = new Date(dueDate + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const pendingCount = visibleTasks.filter(t => t.status !== 'completed').length;
  const myCount = visibleTasks.filter(t => t.status !== 'completed' && t.assignedToId === providerId).length;
  const completedCount = visibleTasks.filter(t => t.status === 'completed').length;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: 'bold', color: '#e5e7eb', margin: 0 }}>
            Tasks
          </h2>
          <p style={{ color: '#9ca3af', fontSize: '14px', marginTop: '4px' }}>
            Assign and track to-dos for your team
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            padding: '10px 20px',
            background: 'linear-gradient(135deg, #10B981, #059669)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          + New Task
        </button>
      </div>

      {/* Filter Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {[
          { key: 'all' as const, label: `All Open (${pendingCount})` },
          { key: 'mine' as const, label: `My Tasks (${myCount})` },
          { key: 'completed' as const, label: `Completed (${completedCount})` },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setViewFilter(tab.key)}
            style={{
              padding: '8px 16px',
              background: viewFilter === tab.key ? '#58A6FF' : '#28527A',
              border: viewFilter === tab.key ? 'none' : '1px solid #6CA6C1',
              borderRadius: '20px',
              color: viewFilter === tab.key ? '#fff' : '#9ca3af',
              fontSize: '13px',
              fontWeight: viewFilter === tab.key ? 600 : 400,
              cursor: 'pointer',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>Loading tasks...</div>
      ) : sortedTasks.length === 0 ? (
        /* Empty State */
        <div style={{
          background: '#1a3a5c',
          borderRadius: '12px',
          padding: '60px 24px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>
            {viewFilter === 'completed' ? '🎉' : '✓'}
          </div>
          <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#e5e7eb', marginBottom: '8px' }}>
            {viewFilter === 'completed' ? 'No completed tasks yet' : 'No open tasks'}
          </h3>
          <p style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '20px' }}>
            {viewFilter === 'completed'
              ? 'Completed tasks will appear here.'
              : 'Create a task and assign it to a team member to get started.'}
          </p>
          {viewFilter !== 'completed' && (
            <button
              onClick={() => setShowCreateModal(true)}
              style={{
                padding: '10px 24px',
                background: 'linear-gradient(135deg, #10B981, #059669)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              + New Task
            </button>
          )}
        </div>
      ) : (
        /* Task List */
        <div style={{ display: 'grid', gap: '12px' }}>
          {sortedTasks.map(task => (
            <div
              key={task.id}
              style={{
                background: task.status === 'completed' ? '#1a3a5c80' : '#1a3a5c',
                padding: '16px 20px',
                borderRadius: '10px',
                border: isOverdue(task.dueDate) && task.status !== 'completed'
                  ? '1px solid #ef4444'
                  : '1px solid transparent',
                opacity: task.status === 'completed' ? 0.7 : 1,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                {/* Complete / Reopen button */}
                <button
                  onClick={() => task.status === 'completed' ? handleReopenTask(task.id) : handleCompleteClick(task.id)}
                  title={task.status === 'completed' ? 'Reopen task' : 'Mark as complete'}
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    border: task.status === 'completed' ? 'none' : '2px solid #6CA6C1',
                    background: task.status === 'completed' ? '#10B981' : 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '2px',
                    flexShrink: 0,
                    padding: 0,
                  }}
                >
                  {task.status === 'completed' && (
                    <CheckCircle size={16} color="#fff" />
                  )}
                </button>

                {/* Task Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: '15px',
                      fontWeight: '600',
                      color: '#e5e7eb',
                      textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                    }}>
                      {task.title}
                    </span>

                    {task.priority !== 'none' && (
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: getPriorityColor(task.priority) + '25',
                        color: getPriorityColor(task.priority),
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                      }}>
                        {task.priority}
                      </span>
                    )}

                    {isOverdue(task.dueDate) && task.status !== 'completed' && (
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: '#ef444425',
                        color: '#ef4444',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '600',
                      }}>
                        OVERDUE
                      </span>
                    )}
                  </div>

                  {task.description && (
                    <p style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '8px', lineHeight: 1.4 }}>
                      {task.description}
                    </p>
                  )}

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', fontSize: '13px', color: '#6CA6C1' }}>
                    {task.assignedToName && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <User size={14} />
                        <span>{task.assignedToName}</span>
                      </div>
                    )}

                    {task.dueDate && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Calendar size={14} />
                        <span style={{ color: isOverdue(task.dueDate) && task.status !== 'completed' ? '#ef4444' : undefined }}>
                          {formatDueDate(task.dueDate)}
                        </span>
                        {task.dueTime && (
                          <>
                            <Clock size={14} style={{ marginLeft: '4px' }} />
                            <span>{task.dueTime}</span>
                          </>
                        )}
                      </div>
                    )}

                    {task.clientName && (
                      <div>Client: <span style={{ color: '#d1d5db' }}>{task.clientName}</span></div>
                    )}

                    {task.assignedByName && task.assignedById !== providerId && (
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        From: {task.assignedByName}
                      </div>
                    )}
                  </div>

                  {/* Completion notes */}
                  {task.status === 'completed' && task.completionNotes && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      background: '#10B98115',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#10B981',
                      borderLeft: '3px solid #10B981',
                    }}>
                      <strong>Notes:</strong> {task.completionNotes}
                    </div>
                  )}
                </div>

                {/* Delete Button */}
                <button
                  onClick={() => { setTaskToDelete(task.id); setShowDeleteModal(true); }}
                  style={{
                    padding: '4px 10px',
                    backgroundColor: '#ef444420',
                    color: '#ef4444',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Task Modal (slide-in panel) */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          justifyContent: 'flex-end',
          zIndex: 1000,
        }}
        onClick={(e) => { if (e.target === e.currentTarget) { setShowCreateModal(false); resetForm(); } }}
        >
          <div style={{
            width: '480px',
            maxWidth: '100vw',
            backgroundColor: '#0f2a3f',
            height: '100vh',
            overflowY: 'auto',
            boxShadow: '-4px 0 20px rgba(0,0,0,0.3)',
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #28527A',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#e5e7eb' }}>Create Task</h2>
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                style={{ padding: '4px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#9ca3af' }}
              >
                <X size={22} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px' }}>
              {/* Task Name */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#9ca3af', marginBottom: '6px' }}>
                  Task Name *
                </label>
                <input
                  type="text"
                  placeholder="e.g., Check with Tonya on her bill"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #28527A',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: '#1a3a5c',
                    color: '#e5e7eb',
                  }}
                />
              </div>

              {/* Description */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '500', color: '#9ca3af' }}>
                    Description
                  </label>
                  <VoiceInputButton onResult={text => setTaskForm(prev => ({ ...prev, description: prev.description + ' ' + text }))} />
                </div>
                <textarea
                  placeholder="Add details about this task..."
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #28527A',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    background: '#1a3a5c',
                    color: '#e5e7eb',
                  }}
                />
              </div>

              {/* Assign To */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#9ca3af', marginBottom: '6px' }}>
                  Assign To
                </label>
                <select
                  value={taskForm.assignedToId}
                  onChange={handleAssigneeChange}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #28527A',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: '#1a3a5c',
                    color: '#e5e7eb',
                  }}
                >
                  <option value="">Unassigned (self)</option>
                  {teamMembers.filter(m => m.status === 'active').map(member => (
                    <option key={member.id} value={member.authUid || member.id}>
                      {member.name} — {member.role}
                    </option>
                  ))}
                </select>
              </div>

              {/* Due Date / Time */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#9ca3af', marginBottom: '6px' }}>
                  Due Date
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <input
                    type="date"
                    value={taskForm.dueDate}
                    onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })}
                    style={{
                      padding: '10px 12px',
                      border: '1px solid #28527A',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: '#1a3a5c',
                      color: '#e5e7eb',
                    }}
                  />
                  <input
                    type="time"
                    value={taskForm.dueTime}
                    onChange={(e) => setTaskForm({ ...taskForm, dueTime: e.target.value })}
                    style={{
                      padding: '10px 12px',
                      border: '1px solid #28527A',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: '#1a3a5c',
                      color: '#e5e7eb',
                    }}
                  />
                </div>
              </div>

              {/* Priority */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#9ca3af', marginBottom: '6px' }}>
                  Priority
                </label>
                <select
                  value={taskForm.priority}
                  onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value as any })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #28527A',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: '#1a3a5c',
                    color: '#e5e7eb',
                  }}
                >
                  <option value="none">None</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              {/* Client (optional) */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#9ca3af', marginBottom: '6px' }}>
                  Related Client (optional)
                </label>
                <select
                  value={taskForm.clientId}
                  onChange={handleClientChange}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #28527A',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: '#1a3a5c',
                    color: '#e5e7eb',
                  }}
                >
                  <option value="">No client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #28527A',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              position: 'sticky',
              bottom: 0,
              background: '#0f2a3f',
            }}>
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  color: '#9ca3af',
                  border: '1px solid #28527A',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTask}
                style={{
                  padding: '10px 24px',
                  background: 'linear-gradient(135deg, #10B981, #059669)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Create Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Task Modal — with notes */}
      {showCompleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
        }}>
          <div style={{
            background: '#0f2a3f',
            borderRadius: '12px',
            padding: '24px',
            width: '440px',
            maxWidth: '90vw',
            border: '1px solid #28527A',
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#e5e7eb', marginBottom: '16px' }}>
              Complete Task
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <p style={{ fontSize: '14px', color: '#9ca3af', margin: 0 }}>
                Add any notes about how this task was completed (optional):
              </p>
              <VoiceInputButton onResult={text => setCompletionNotes(prev => prev + ' ' + text)} />
            </div>
            <textarea
              placeholder="e.g., Spoke with patient, bill adjusted to reflect insurance co-pay..."
              value={completionNotes}
              onChange={(e) => setCompletionNotes(e.target.value)}
              rows={4}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #28527A',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
                background: '#1a3a5c',
                color: '#e5e7eb',
                marginBottom: '20px',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => { setShowCompleteModal(false); setTaskToComplete(null); }}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  color: '#9ca3af',
                  border: '1px solid #28527A',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmComplete}
                style={{
                  padding: '10px 24px',
                  background: 'linear-gradient(135deg, #10B981, #059669)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Mark Complete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setTaskToDelete(null); }}
        onConfirm={confirmDeleteTask}
        title="Delete Task"
        message="Are you sure you want to delete this task? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default TasksTab;
